import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useFilterOptions } from '../../hooks';
import type { FilterFieldConfig, FilterOption } from '../../types';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface SelectFilterProps {
  /** Field configuration */
  config: FilterFieldConfig<string>;
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Hide the label */
  hideLabel?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Single select dropdown filter field.
 *
 * @example
 * ```tsx
 * <SelectFilter
 *   config={{
 *     key: 'departmentId',
 *     type: 'select',
 *     label: 'Department',
 *     placeholder: 'All departments',
 *     defaultValue: 'all',
 *     options: 'departments',
 *   }}
 *   value={filters.departmentId}
 *   onChange={(value) => setFilter('departmentId', value)}
 * />
 * ```
 */
export function SelectFilter({
  config,
  value,
  onChange,
  disabled = false,
  className,
  hideLabel = false,
}: SelectFilterProps) {
  const { options, isLoading } = useFilterOptions(config.options);

  // Determine placeholder text
  const placeholder = config.placeholder ?? `All ${config.label.toLowerCase()}`;

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        {!hideLabel && (
          <label className="text-sm font-medium text-foreground">
            {config.label}
          </label>
        )}
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {!hideLabel && (
        <label className="text-sm font-medium text-foreground">
          {config.label}
        </label>
      )}
      <Select
        value={value}
        onValueChange={onChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {/* "All" option */}
          <SelectItem value="all">{placeholder}</SelectItem>

          {/* Dynamic options */}
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {config.renderOption ? (
                config.renderOption(option)
              ) : (
                <SelectOptionContent option={option} />
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

interface SelectOptionContentProps {
  option: FilterOption;
}

/**
 * Default option content renderer.
 * Shows label and optional count.
 */
function SelectOptionContent({ option }: SelectOptionContentProps) {
  return (
    <span className="flex items-center gap-2">
      {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
      <span>{option.label}</span>
      {option.count !== undefined && (
        <span className="ml-auto text-muted-foreground">({option.count})</span>
      )}
    </span>
  );
}
